import type { ComboboxItem } from '@/components'
import {
  Button,
  Card,
  Combobox,
  DatePicker,
  FAIcon,
  Field,
  FieldLabel,
  Input,
  UserCombobox,
  UserDocumentCategoryCombobox,
  useExpirationStatusLabels,
} from '@/components'
import { cn } from '@/lib/utils'
import { expirationStatuses } from '@/modules/common/expiration-status'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { hasActiveUserDocumentFilter } from './filter-variables'
import type { UserDocumentListFilterForm } from './types'

interface Props {
  filter: UserDocumentListFilterForm
  searchPlaceholder: string

  onChange(patch: Partial<UserDocumentListFilterForm>): void
  onClear(): void
}

export const UserDocumentFilters: React.FC<Props> = ({
  filter,
  searchPlaceholder,

  onChange,
  onClear,
}) => {
  const translations = useTranslate({
    filter: 'common.filter',
    search: 'common.search-filter',
    users: 'common.users',
    category: 'common.category',
    responsibleUser: 'common.responsible-user',
    status: 'common.status',
    expirationDate: 'documents.expiration-date',
    select: 'common.select-ellipsis',
    clear: 'common.clear-filters',
  })

  const statusLabels = useExpirationStatusLabels()
  const statusItems = useMemo<
    ComboboxItem<UserDocumentListFilterForm['expirationStatus']>[]
  >(
    () =>
      expirationStatuses.map(status => ({
        value: status,
        label: statusLabels[status],
      })),
    [statusLabels]
  )

  const [collapsed, setCollapsed] = useState(isMobileOnly)
  const showFilters = !isMobileOnly || !collapsed

  return (
    <Card className="mb-4 p-0" contentClassName="p-0">
      {isMobileOnly && (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-sm font-semibold text-stone-600"
          aria-expanded={showFilters}
          onClick={() => setCollapsed(value => !value)}
        >
          {translations.filter}
          <FAIcon
            className={cn('transition-transform', showFilters && 'rotate-180')}
            icon="chevron-down"
          />
        </button>
      )}

      {showFilters && (
        <form
          className={cn('grid gap-4 p-4', 'md:flex md:flex-wrap md:items-end')}
          onSubmit={event => event.preventDefault()}
        >
          <Field className="md:w-[320px]">
            <FieldLabel htmlFor="user-document-search">
              {translations.search}
            </FieldLabel>
            <Input
              id="user-document-search"
              type="search"
              value={filter.q}
              placeholder={searchPlaceholder}
              onChange={value => onChange({ q: value })}
            />
          </Field>

          <Field>
            <FieldLabel>{translations.users}</FieldLabel>
            <UserCombobox
              mode="multiple"
              value={filter.users}
              className="max-w-[350px]"
              placeholder={translations.select}
              onChange={value => onChange({ users: value })}
            />
          </Field>

          <Field>
            <FieldLabel>{translations.category}</FieldLabel>
            <UserDocumentCategoryCombobox
              mode="multiple"
              value={filter.categories}
              className="max-w-[350px]"
              placeholder={translations.select}
              onChange={value => onChange({ categories: value })}
            />
          </Field>

          <Field>
            <FieldLabel>{translations.status}</FieldLabel>
            <Combobox
              mode="single"
              value={filter.expirationStatus}
              items={statusItems}
              clearable
              noSearch
              placeholder={translations.select}
              onChange={value => onChange({ expirationStatus: value })}
            />
          </Field>

          <Field>
            <FieldLabel>{translations.expirationDate}</FieldLabel>
            <DatePicker
              mode="range"
              value={filter.dateRange}
              format="PP"
              clearable
              placeholder={translations.select}
              onChange={value => onChange({ dateRange: value })}
            />
          </Field>

          <Field>
            <FieldLabel>{translations.responsibleUser}</FieldLabel>
            <UserCombobox
              mode="multiple"
              value={filter.responsibleUsers}
              className="max-w-[350px]"
              placeholder={translations.select}
              onChange={value => onChange({ responsibleUsers: value })}
            />
          </Field>

          {hasActiveUserDocumentFilter(filter) && (
            <Button variant="ghost" size="lg" type="button" onClick={onClear}>
              <FAIcon icon="times" />
              {translations.clear}
            </Button>
          )}
        </form>
      )}
    </Card>
  )
}
